import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Chip, Button, Accordion, AccordionSummary, AccordionDetails, Tooltip, IconButton } from '@mui/material';
import {
  Thermostat as ThermostatIcon,
  Opacity as OpacityIcon,
  Air as AirIcon,
  PlayArrow as PlayArrowIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Whatshot as FireIcon,
  LocalFireDepartment as ClimateIcon,
  BookmarkAdded as BookmarkAdded,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { getExtremeWeatherDays } from '../../api/weatherHistory';
import { useClimateExtremeScenarios } from '../../api/climateProjections';
import { useWeatherPresets, useDeleteWeatherPreset } from '../../api/weatherPresets';
import { useUnitSystem } from '../../hooks/useUnitSystem';
import { formatSpeed, formatTemperature } from '../../utils/units';

interface RunSimulationsViewProps {
  projectId: string;
  onPresetSelect?: (preset: any) => void;
}


const RunSimulationsView: React.FC<RunSimulationsViewProps> = ({ projectId, onPresetSelect }) => {
  const unitSystem = useUnitSystem();
  const [extremeDays, setExtremeDays] = useState<any[]>([]);
  const [extremeDaysLimit, setExtremeDaysLimit] = useState(5);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { data: climateScenarios = [], isLoading: isLoadingClimate } = useClimateExtremeScenarios(projectId);
  const { data: userPresetsResponse, isLoading: isLoadingPresets } = useWeatherPresets();
  const deletePresetMutation = useDeleteWeatherPreset();
  const userPresets = userPresetsResponse?.data ?? [];


  useEffect(() => {
    const fetchExtremeDays = async () => {
      const days = await getExtremeWeatherDays(projectId, extremeDaysLimit);
      setExtremeDays(days);
    };
    fetchExtremeDays();
  }, [projectId, extremeDaysLimit]);

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    setExtremeDaysLimit(prev => prev + 5);
    setIsLoadingMore(false);
  };


  const getDisplayDate = (day: any): Date => {
    if (day.timestamp) return new Date(day.timestamp);
    return new Date(day.date + 'T10:00:00');
  };

  const formatDate = (day: any) => {
    return getDisplayDate(day).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getFDIColor = (fdi: number) => {
    if (fdi >= 100) return '#b71c1c';
    if (fdi >= 50) return '#f44336';
    if (fdi >= 25) return '#ff9800';
    if (fdi >= 12) return '#ffeb3b';
    return '#4caf50';
  };

  const getFDILabel = (fdi: number) => {
    if (fdi >= 100) return 'Catastrophic';
    if (fdi >= 50) return 'Extreme';
    if (fdi >= 25) return 'Very High';
    if (fdi >= 12) return 'High';
    return 'Moderate';
  };

  return (
    <Box>
      {/* Info Banner */}
      <Box
        sx={{
          bgcolor: 'rgba(33, 150, 243, 0.1)',
          border: '1px solid rgba(33, 150, 243, 0.3)',
          borderRadius: 2,
          p: 2,
          mb: 3,
          display: 'flex',
          gap: 2,
        }}
      >
        <InfoIcon sx={{ color: '#2196f3', mt: 0.5 }} />
        <Box>
          <Typography variant="h6" sx={{ color: '#fff', mb: 0.5 }}>
            Simulation Scenarios
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Select a weather profile below to instantly configure the simulation engine. Choose between statistical
            baselines or reconstruct historical extreme events.
          </Typography>
        </Box>
      </Box>

      {/* My Presets Section */}
      <Accordion
        defaultExpanded
        sx={{
          bgcolor: 'rgba(76, 175, 80, 0.05)',
          '&:before': { display: 'none' },
          mb: 2,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#4caf50' }} />}
          sx={{
            borderBottom: '1px solid rgba(76, 175, 80, 0.2)',
            '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.08)' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BookmarkAdded sx={{ color: '#4caf50' }} />
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600 }}>
              My Presets
            </Typography>
            <Chip
              label={userPresets.length}
              size="small"
              sx={{ ml: 1, bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4caf50' }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          {isLoadingPresets ? (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Loading presets...</Typography>
          ) : userPresets.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
              No saved presets yet. Use "Save Preset" from Run Date or Run Custom to create one.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {userPresets.map((preset: any) => (
                <Card
                  key={preset.id}
                  sx={{
                    bgcolor: 'rgba(76, 175, 80, 0.08)',
                    border: '1px solid rgba(76, 175, 80, 0.25)',
                    '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.13)', borderColor: '#4caf50' },
                  }}
                >
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {preset.preset_type === 'date' ? (
                          <CalendarIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                        ) : (
                          <BuildIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                        )}
                        <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 600 }}>
                          {preset.name}
                        </Typography>
                        <Chip
                          label={preset.preset_type === 'date' ? 'Date' : 'Custom'}
                          size="small"
                          sx={{ bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4caf50', fontSize: '0.65rem' }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                        {preset.preset_type === 'date'
                          ? `Date: ${preset.params?.date ?? '—'}  ·  DF: ${preset.params?.drought_factor ?? '—'}  ·  Curing: ${preset.params?.curing ?? '—'}%`
                          : `Temp: ${preset.params?.temperature != null ? formatTemperature(preset.params.temperature, unitSystem) : '—'}  ·  RH: ${preset.params?.humidity ?? '—'}%  ·  Wind: ${preset.params?.wind_speed != null ? formatSpeed(preset.params.wind_speed, unitSystem) : '—'}  ·  DF: ${preset.params?.drought_factor ?? '—'}`
                        }
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => onPresetSelect?.(preset)}
                        sx={{
                          borderColor: '#4caf50',
                          color: '#4caf50',
                          '&:hover': { borderColor: '#66bb6a', bgcolor: 'rgba(76, 175, 80, 0.1)' },
                          minWidth: 90,
                        }}
                      >
                        Select
                      </Button>
                      <Tooltip title="Delete preset" arrow>
                        <IconButton
                          size="small"
                          onClick={() => deletePresetMutation.mutate(preset.id)}
                          disabled={deletePresetMutation.isPending}
                          sx={{ color: 'rgba(255, 82, 82, 0.7)', '&:hover': { color: '#ff5252' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Climate Projections Section */}
      <Accordion 
        defaultExpanded={false}
        sx={{ 
          bgcolor: 'rgba(255, 107, 53, 0.05)',
          '&:before': { display: 'none' },
          mb: 2,
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon sx={{ color: '#ff6b35' }} />}
          sx={{ 
            borderBottom: '1px solid rgba(255, 107, 53, 0.2)',
            '&:hover': { bgcolor: 'rgba(255, 107, 53, 0.08)' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClimateIcon sx={{ color: '#ff6b35' }} />
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600 }}>
              Climate Projections
            </Typography>
            <Chip 
              label={climateScenarios.length} 
              size="small" 
              sx={{ ml: 1, bgcolor: 'rgba(255, 107, 53, 0.2)', color: '#ff6b35' }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 3 }}>
          {isLoadingClimate ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography variant="body2" color="text.secondary">Loading climate scenarios...</Typography>
            </Box>
          ) : climateScenarios.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
              No climate projection data available yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {climateScenarios.map((scenario: any, index: number) => (
                <Card
                  key={index}
                  sx={{
                    bgcolor: 'rgba(255, 107, 53, 0.1)',
                    border: '1px solid rgba(255, 107, 53, 0.3)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 107, 53, 0.15)',
                      borderColor: '#ff6b35',
                    },
                  }}
                >
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                          PROJECTION
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#fff' }}>
                          {scenario.decade} Extreme
                        </Typography>
                        <Chip
                          label={getFDILabel(scenario.fire_danger_rating)}
                          size="small"
                          sx={{
                            bgcolor: getFDIColor(scenario.fire_danger_rating),
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                        {scenario.model_name && (
                          <Tooltip title="Climate model used for this projection" arrow>
                            <Chip
                              label={scenario.model_name}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: '0.65rem',
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                        {scenario.temperature != null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ThermostatIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)' }} />
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {formatTemperature(scenario.temperature, unitSystem)}
                            </Typography>
                          </Box>
                        )}
                        {scenario.humidity != null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <OpacityIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)' }} />
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {scenario.humidity.toFixed(0)}% RH
                            </Typography>
                          </Box>
                        )}
                        {scenario.wind_speed != null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AirIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)' }} />
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {formatSpeed(scenario.wind_speed, unitSystem)}
                            </Typography>
                          </Box>
                        )}
                        {scenario.drought_factor != null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 152, 0, 0.7)', fontWeight: 600 }}>
                              DF:
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {scenario.drought_factor.toFixed(1)}
                            </Typography>
                          </Box>
                        )}
                        {scenario.fire_danger_rating != null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FireIcon sx={{ fontSize: 16, color: getFDIColor(scenario.fire_danger_rating) }} />
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {scenario.fire_danger_rating.toFixed(1)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => onPresetSelect?.(scenario)}
                      sx={{
                        borderColor: '#ff6b35',
                        color: '#ff6b35',
                        '&:hover': {
                          borderColor: '#ff8a50',
                          bgcolor: 'rgba(255, 107, 53, 0.1)',
                        },
                        minWidth: 120,
                      }}
                    >
                      Select
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Historical Extreme Days Section */}
      <Accordion 
        defaultExpanded={false}
        sx={{ 
          bgcolor: 'rgba(211, 47, 47, 0.05)',
          '&:before': { display: 'none' },
          mb: 2,
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon sx={{ color: '#d32f2f' }} />}
          sx={{ 
            borderBottom: '1px solid rgba(211, 47, 47, 0.2)',
            '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.08)' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" sx={{ fontSize: 20 }}>🔥</Box>
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600 }}>
              Historical Extreme Days
            </Typography>
            <Chip 
              label={extremeDays.length} 
              size="small" 
              sx={{ ml: 1, bgcolor: 'rgba(211, 47, 47, 0.2)', color: '#d32f2f' }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {extremeDays.map((day, index) => (
          <Card
            key={index}
            sx={{
              bgcolor: 'rgba(211, 47, 47, 0.1)',
              border: '1px solid rgba(211, 47, 47, 0.3)',
              '&:hover': {
                bgcolor: 'rgba(211, 47, 47, 0.15)',
                borderColor: '#d32f2f',
              },
            }}
          >
            <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    HISTORY
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#fff' }}>
                    {formatDate(day)}
                  </Typography>
                  <Chip
                    label={getFDILabel(day.fire_danger_rating)}
                    size="small"
                    sx={{
                      bgcolor: getFDIColor(day.fire_danger_rating),
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ThermostatIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {formatTemperature(day.temperature, unitSystem)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <OpacityIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {day.relative_humidity.toFixed(0)}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AirIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {formatSpeed(day.wind_speed, unitSystem)} NW
                    </Typography>
                  </Box>
                  {day.drought_factor !== null && day.drought_factor !== undefined && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 152, 0, 0.7)', fontWeight: 600 }}>
                        DF:
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {day.drought_factor.toFixed(1)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={() => onPresetSelect?.({
                  ...day,
                  timestamp: day.timestamp || (day.date + 'T10:00:00'),
                })}
                sx={{
                  borderColor: '#d32f2f',
                  color: '#d32f2f',
                  '&:hover': {
                    borderColor: '#f44336',
                    bgcolor: 'rgba(211, 47, 47, 0.1)',
                  },
                  minWidth: 120,
                }}
              >
                Select
              </Button>
            </CardContent>
          </Card>
        ))}
          </Box>

          {/* Load More Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              sx={{
                borderColor: 'rgba(211, 47, 47, 0.5)',
                color: '#d32f2f',
                '&:hover': {
                  borderColor: '#d32f2f',
                  bgcolor: 'rgba(211, 47, 47, 0.1)',
                },
                minWidth: 200,
              }}
            >
              {isLoadingMore ? 'Loading...' : 'Load More Extreme Days'}
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default RunSimulationsView;
